import { useState } from "react";
import { toast } from "sonner";
import { MagnifyingGlass, Polygon as PolyIcon, Circle as CircleIcon, ArrowCounterClockwise } from "@phosphor-icons/react";
import { Geo } from "../lib/api";
import { LocateMap } from "../components/LocateMap";
import { AddressSearch } from "../components/AddressSearch";
import { LocateResults } from "../components/LocateResults";

const RADII = [100, 250, 500, 1000, 5000];

export default function LocatePage() {
  const [mode, setMode] = useState("area");
  const [polygon, setPolygon] = useState([]);
  const [center, setCenter] = useState(null);
  const [radius, setRadius] = useState(250);
  const [results, setResults] = useState(null);
  const [fit, setFit] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const reset = () => { setPolygon([]); setCenter(null); setResults(null); setSelected(null); };
  const switchMode = (m) => { setMode(m); reset(); };
  const click = (pt) => (mode === "area" ? setPolygon((p) => [...p, pt]) : setCenter(pt));
  const pickAddress = (h) => { setFit([[h.lat, h.lon]]); if (mode === "radius") setCenter([h.lat, h.lon]); };
  const ready = mode === "area" ? polygon.length >= 3 : !!center;

  const search = async () => {
    setBusy(true);
    try {
      const body = mode === "area" ? { mode: "polygon", polygon } : { mode: "radius", lat: center[0], lon: center[1], radius_m: radius };
      const r = await Geo.search(body);
      setResults(r);
      if (r.count) setFit(r.photos.map((p) => [p.lat, p.lon]));
    } catch (e) {
      toast.error(e?.response?.data?.detail?.toString?.() || "Search failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="px-4 py-6 max-w-6xl mx-auto page-fade" data-testid="locate-page">
      <p className="label-mono text-neutral-500">LOCATION SEARCH</p>
      <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter flex items-center gap-3"><MagnifyingGlass weight="bold" /> FIND BY PLACE</h1>
      <p className="text-sm text-neutral-600 mt-2 max-w-2xl">Find every GPS-tagged photo across your sessions inside an area you draw, or within a distance of a point or address.</p>
      <div className="mt-6 grid lg:grid-cols-[1fr_340px] gap-4">
        <LocateMap mode={mode} polygon={polygon} center={center} radius={radius} results={results} fit={fit} onClick={click} selected={selected} />
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => switchMode("area")} className={mode === "area" ? "btn-flat" : "btn-flat-outline"} data-testid="locate-mode-area"><PolyIcon weight="bold" size={16} /> Draw area</button>
            <button onClick={() => switchMode("radius")} className={mode === "radius" ? "btn-flat" : "btn-flat-outline"} data-testid="locate-mode-radius"><CircleIcon weight="bold" size={16} /> Near point</button>
          </div>
          <AddressSearch onPick={pickAddress} />
          <p className="text-xs text-neutral-500 font-mono" data-testid="locate-hint">
            {mode === "area" ? `TAP THE MAP TO ADD CORNERS · ${polygon.length} ADDED${polygon.length < 3 ? ` (NEED ${3 - polygon.length} MORE)` : ""}` : center ? `CENTER ${center[0].toFixed(5)}, ${center[1].toFixed(5)}` : "TAP THE MAP OR FIND AN ADDRESS TO SET THE CENTER"}
          </p>
          {mode === "radius" && (
            <div className="flex flex-wrap gap-2" data-testid="locate-radius-options">
              {RADII.map((r) => (
                <button key={r} onClick={() => setRadius(r)} className={`chip transition-colors ${radius === r ? "!border-cyan-400 !text-cyan-300 !bg-cyan-400/15" : "opacity-70"}`} data-testid={`locate-radius-${r}`}>{r >= 1000 ? `${r / 1000} km` : `${r} m`}</button>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <button onClick={search} disabled={!ready || busy} className="btn-flat flex-1 justify-center" data-testid="locate-search-btn"><MagnifyingGlass weight="bold" size={16} /> {busy ? "Searching…" : "Search photos"}</button>
            <button onClick={reset} className="btn-flat-outline" data-testid="locate-reset-btn"><ArrowCounterClockwise weight="bold" size={16} /></button>
          </div>
          <LocateResults results={results} selected={selected} onSelect={(p) => { setSelected(p.id); setFit([[p.lat, p.lon]]); }} />
        </div>
      </div>
    </div>
  );
}
